import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { ResourceNotice, PrintBar } from "@/components/ResourceNotice";
import { useResourcePrint } from "@/pages/ColoringSheet";

const Option = ({ r }) => (
  <li className="flex gap-3 text-[13px] leading-snug print:text-[11.5px]">
    <span className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-full border-2 border-slate-500" />
    <span><span className="font-semibold text-slate-900">{r.name}:</span> <span className="text-slate-700">{r.text}</span></span>
  </li>
);

export const Topic = ({ t, n }) => (
  <article className="avoid-break border-b border-slate-200 py-5 print:py-3" data-testid={`selfeval-topic-${t.key}`}>
    <h2 className="font-display text-lg font-bold text-slate-900">{n}. {t.title}</h2>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{t.description}</p>
    <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#1F4266]">Choose the one statement that fits you best right now</p>
    <ul className="mt-2 space-y-2">
      {t.ratings.map((r) => <Option key={r.name} r={r} />)}
    </ul>
    <div className="mt-3 flex items-end gap-2 text-xs text-slate-500">
      My notes:<span className="h-5 flex-1 border-b border-slate-400" />
    </div>
  </article>
);

export const Intro = () => (
  <>
    <div className="flex items-center gap-3">
      <img src="/flower-logo.png" alt="" className="h-10 w-10" />
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Minister's Helper · Self-Evaluation Workbook</div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Rate Yourself: 25 Topics</h1>
      </div>
    </div>
    <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-700" data-testid="selfeval-instructions">
      <p className="font-semibold text-slate-900">How to use this workbook</p>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        <li>Go through the topics in order. Read the topic description, then read all five statements before choosing.</li>
        <li>Mark the one statement that best describes you right now, not where you hope to be. Take your time and be honest with yourself.</li>
        <li>Use "My notes" for anything you want to remember or talk about with a coach.</li>
        <li>When all 25 topics are done, turn to the DIY Assessment &amp; Scoring Worksheet. It uses the same numbering, so you can copy each choice across and work out your scores.</li>
      </ol>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-10 gap-y-2 text-sm text-slate-700">
      <span>Name: ______________________________</span>
      <span>Date: ________________</span>
    </div>
  </>
);

export default function SelfEvaluationWorkbook() {
  useResourcePrint();
  const [topics, setTopics] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/coaching-guide").then((r) => setTopics(r.data)).catch((e) => setError(errMsg(e)));
  }, []);

  return (
    <div className="resource-doc mx-auto max-w-[8.5in]" data-testid="self-evaluation-workbook">
      <PrintBar title="Self-Evaluation Workbook" />
      <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm print-plain">
        <Intro />
        {error && <p className="mt-6 text-sm text-red-600">{error}</p>}
        <div className="mt-2">{topics?.map((t, i) => <Topic key={t.key} t={t} n={i + 1} />)}</div>
        {topics && <ResourceNotice />}
      </section>
    </div>
  );
}
